import { NavLink } from 'react-router-dom';
import type { BrandFeature, TopNavMenuItem } from '@/brands';
import { brand, isMenuLinkPainted, useBrandFeatures, useBrandMenus } from '@/brands';

// Three filters, and an item renders only when all three pass:
//   `build`   — does this build ship the product at all (compiled-in);
//   `grant`   — did the super admin grant the brand that product (Features);
//   `menu`    — did they then choose to paint this particular link (Menus).
// The first is a constant, the other two arrive with GET /brand. Losing the
// grant also closes the URL behind the link — see `FeatureGate`.
//
// The menu half asks `isMenuLinkPainted` rather than reading the toggle straight,
// because several of these paths are shared with the left rail: `/casino/all/live`
// is this strip's "Live Casino" and the rail's "Live Casino" AND "Our VIP Casino".
// Hiding any one of them closes the page (`isRouteHidden`), so the others must
// stop drawing too or they would point at a 404.
type NavItem = {
  label: string;
  path: string;
  build?: keyof typeof brand.features;
  grant?: BrandFeature;
  menu: TopNavMenuItem;
};

const allNavItems: NavItem[] = [
  { label: 'Home', path: '/home', menu: 'home' },
  { label: 'Casino', path: '/casino/all', build: 'aggregator', grant: 'casino', menu: 'casino' },
  { label: 'Slots', path: '/casino/all/slots', build: 'aggregator', grant: 'casino', menu: 'slots' },
  { label: 'Live Casino', path: '/casino/all/live', build: 'aggregator', grant: 'casino', menu: 'liveCasino' },
  { label: 'Table Games', path: '/casino/all/table', build: 'aggregator', grant: 'casino', menu: 'tableGames' },
  { label: 'Crash', path: '/casino/all/crash', build: 'aggregator', grant: 'casino', menu: 'crash' },
  // Lottery is its own Product in the panel, so it survives Casino being off.
  { label: 'Lottery', path: '/casino/all/lottery', build: 'aggregator', grant: 'lottery', menu: 'lottery' },
  { label: 'Inplay', path: '/inplay', grant: 'sports', menu: 'inplay' },
  { label: 'Cricket', path: '/all-sports/Cricket', grant: 'sports', menu: 'cricket' },
  { label: 'Tennis', path: '/all-sports/Tennis', grant: 'sports', menu: 'tennis' },
  { label: 'Football', path: '/all-sports/Soccer', grant: 'sports', menu: 'football' },
  { label: 'Table Tennis', path: '/all-sports/Table-tennis', grant: 'sports', menu: 'tableTennis' },
];

// The build half is a compile-time constant, so it stays at module scope. The
// grant and menu halves cannot: both arrive with GET /brand, after first paint.
const buildNavItems = allNavItems.filter(
  (item) => !item.build || brand.features[item.build]
);

// Table Tennis used to sit in a `notAllowedRoutes` array whose only effect was to
// preventDefault() its click — a button that looked live and did nothing. The
// backend's SportType enum has no table tennis, so the page now shows the same
// "No matches available" every other dataless sport shows, and an operator who
// does not want that switches the entry off under Menu Visibility.

const Navbar = () => {
  const menus = useBrandMenus();
  const features = useBrandFeatures();
  const navItems = buildNavItems.filter(
    (item) =>
      (!item.grant || features[item.grant] !== false) &&
      isMenuLinkPainted(item.path, menus.topNav[item.menu] !== false, menus)
  );

  return (
    <nav className="hidden xl:flex spt-nav">
      <style>{`
        .spt-nav {
          /* dark navy background */
          background: #0d1b2a;
          border-bottom: 1px solid rgba(255,255,255,0.06);
          width: 100%;
        }
        .spt-nav-item {
          flex: 1 1 0;
          min-width: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 10px 4px;
          font-family: 'Roboto Condensed', sans-serif;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          white-space: nowrap;
          color: #ffffff;
          border-right: 1px solid rgba(255,255,255,0.04);
          transition: background .15s, color .15s;
        }
        .spt-nav-item:last-child { border-right: none; }
        .spt-nav-item:hover { background: rgba(255,255,255,0.05); color: #fff; }
        .spt-nav-item.active {
          color: #fff;
          /* light blue box highlight */
          background: rgba(43,180,238,0.18);
          box-shadow: inset 0 -2px 0 #2bd4ee;
        }
        .spt-nav-lbl {
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
        }
      `}</style>

      {navItems.map((item) => (
        <NavLink
          key={item.label}
          to={item.path}
          className={({ isActive }) => `spt-nav-item${isActive ? ' active' : ''}`}
        >
          <span className="spt-nav-lbl">{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );
};

export default Navbar;
